import React, { useState, useEffect } from 'react';
import {
  handleDeleteBillingInfo,
  handleFetchPlans,
  handleRedeemCoupon,
  handleUpdatePlan,
} from '../../common/subscription/SubscriptionService';
import {
  Stack,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  Grid,
  TextField,
} from '@mui/material';
import { styled } from '@mui/system';
import { showSuccessSnackbar } from '../../common/snackbar/SnackbarActions';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import PlanCard from '../../common/components/PlanCard';
import {
  formatRecurlyPlanInterval,
  formatRecurlyPrice,
} from '../../common/utils/formatFunctions';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
}));

const LeftItem = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  borderRadius: 0,
  margin: theme.spacing(0.5),
  width: '98%',
  textAlign: 'left',
}));

const districtPlanValidationSchema = Yup.object().shape({
  couponCode: Yup.string().required('Required').nullable(),
});

export default function ChangePlanForm({ callback = () => {} }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPlan, setSelecetedPlan] = useState({});
  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    setLoading(true);
    handleFetchPlans()
      .then((res) => {
        if (res) {
          res.sort(function (a, b) {
            return a.DisplayOrder - b.DisplayOrder;
          });
          setAvailablePlans(res);
        } else {
          setAvailablePlans({});
        }
      })
      .catch((err) => {
        console.error(err);
        setErrorMessage(err.message);
        setAvailablePlans({});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleClearClick = () => {
    setSelecetedPlan({});
  };

  const handleConfirmClick = () => {
    setSubmitting(true);
    if (selectedPlan.PlanCode) {
      handleUpdatePlan(selectedPlan.PlanCode)
        .then(() => {
          showSuccessSnackbar('Subscription plan change successfull');
          callback();
        })
        .catch((err) => {
          console.error(err);
          setErrorMessage(err.message);
          setSubmitting(false);
        });
    } else {
      console.error('No plan was selected');
      setErrorMessage('No plan was selected');
      setSubmitting(false);
    }
  };

  return (
    <>
      {loading ? (
        <Grid container spacing={10}>
          <Grid item xs={12}>
            <CircularProgress color='secondary' />
          </Grid>
        </Grid>
      ) : availablePlans && availablePlans.length > 0 ? (
        <Stack alignItems='center' justifyContent='center'>
          {selectedPlan.PlanCode ? (
            selectedPlan.PlanCode === 'district' ? (
              <>
                <Formik
                  initialValues={{
                    couponCode: '',
                  }}
                  validationSchema={districtPlanValidationSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    if (selectedPlan.PlanCode) {
                      handleRedeemCoupon(values.couponCode)
                        .then(() => {
                          handleDeleteBillingInfo();
                          handleUpdatePlan(selectedPlan.PlanCode)
                            .then(() => {
                              showSuccessSnackbar(
                                'Subscription plan change successfull'
                              );
                              callback();
                            })
                            .catch((err) => {
                              console.error(err);
                              setErrorMessage(err.message);
                              setSubmitting(false);
                            });
                        })
                        .catch((err) => {
                          let errMsg =
                            'An error has occurred, if it persists please contact us'; //Default message
                          if (err.response !== undefined) {
                            errMsg = err.response.data['Error']
                              ? err.response.data['Error']
                              : err.response.data['ValidationError']
                              ? 'The sign up code provided is not valid'
                              : 'An error has occurred, if it persists please contact us';
                          } else if (err.message !== undefined) {
                            errMsg = err.message;
                          }
                          console.error(err);
                          console.error(errMsg);
                          setErrorMessage(errMsg);
                          setSubmitting(false);
                        });
                    } else {
                      console.error('No plan was selected');
                      setErrorMessage('No plan was selected');
                      setSubmitting(false);
                    }
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    setFieldValue,
                  }) => {
                    return (
                      <Form>
                        <Stack alignItems='center' justifyContent='center'>
                          <LeftItem>
                            <Typography>
                              <b>Selected Plan: {selectedPlan.PlanName}</b>
                              <br />
                              {selectedPlan.PlanDescription}
                            </Typography>
                          </LeftItem>
                          <LeftItem>
                            <Typography>
                              <b>Plan Cost</b>
                              <br />
                              Pre paid by your district.
                            </Typography>
                          </LeftItem>
                          <Item>
                            <Button
                              color='tertiary'
                              variant='contained'
                              onClick={handleClearClick}
                              type={'reset'}
                            >
                              Select Different Plan
                            </Button>
                          </Item>

                          <Typography sx={{ textAlign: 'left' }}>
                            <b>Sign Up Code</b>
                            <br />
                            Please enter the code from your administration to
                            switch to the district plan.
                          </Typography>
                          <TextField
                            name='couponCode'
                            type='text'
                            label='Sign Up Code'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.couponCode}
                            placeholder='Sign Up Code'
                            variant='standard'
                            error={errors.couponCode && touched.couponCode}
                            helperText={touched.couponCode && errors.couponCode}
                            sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                            inputProps={{ maxLength: 64 }}
                          />
                          <LeftItem>
                            <Typography variant='subtitle2'>
                              When you click confirm, a pending plan change will
                              be added to your subcription and the change will
                              take effect at the end of your current billing
                              period. You can cancel this pending change at any
                              point up until it becomes effective. Your personal
                              billing details will be deleted from your account
                              because you are switching to a plan pre-paid by
                              your district. If you cancel this change you will
                              need to re-enter your credit card info on the
                              billling page.
                            </Typography>
                          </LeftItem>
                          <Box sx={{ m: 1, position: 'relative' }}>
                            <Button
                              sx={{ marginTop: 1 }}
                              type={'submit'}
                              disabled={isSubmitting}
                              variant='contained'
                            >
                              <SaveIcon style={{ marginRight: '5' }} />
                              Confirm Plan Change
                            </Button>
                            {isSubmitting && (
                              <CircularProgress
                                size={24}
                                sx={{
                                  color: 'secondary.main',
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  marginTop: '-6px',
                                  marginLeft: '-12px',
                                }}
                              />
                            )}
                          </Box>
                        </Stack>
                      </Form>
                    );
                  }}
                </Formik>
              </>
            ) : (
              <>
                <LeftItem>
                  <Typography>
                    <QuestionMarkIcon
                      sx={{ color: 'primary.main' }}
                      style={{ verticalAlign: 'middle' }}
                    />
                    Please confirm changing your subscription plan to the
                    selected plan below.
                  </Typography>
                </LeftItem>
                <LeftItem>
                  <Typography>
                    <b>New Selected Plan: {selectedPlan.PlanName}</b>
                    <br />
                    {selectedPlan.PlanDescription}
                  </Typography>
                </LeftItem>
                {selectedPlan.FreeTrialLength > 0 && (
                  <LeftItem>
                    <Typography>
                      <b>Free Trial Length</b>
                      <br />
                      {selectedPlan.FreeTrialLength +
                        ' ' +
                        selectedPlan.FreeTrialInterval}
                    </Typography>
                  </LeftItem>
                )}
                <LeftItem>
                  <Typography>
                    <b>
                      New Plan Cost
                      {selectedPlan.FreeTrialLength > 0 &&
                        ' (After Free Trial)'}
                    </b>
                    <br />
                    {formatRecurlyPrice(
                      selectedPlan.PlanUnitCost,
                      selectedPlan.PlanCurrencyCode
                    ) +
                      ' ' +
                      formatRecurlyPlanInterval(
                        selectedPlan.PlanInterval,
                        selectedPlan.PlanIntervalLength
                      )}
                  </Typography>
                </LeftItem>
                <Item>
                  <Button
                    color='tertiary'
                    variant='contained'
                    onClick={handleClearClick}
                    disabled={submitting}
                  >
                    Select Different Plan
                  </Button>
                </Item>
                <LeftItem>
                  <Typography variant='subtitle2'>
                    When you click confirm, a pending plan change will be added
                    to your subcription and the change will take effect at the
                    end of your current billing period. You can cancel this
                    pending change at any point up until it becomes effective.
                    The payment method on file will be automatically charged
                    when the change takes effect and on a recurring basis per
                    the terms of the selected plan unless you cancel the
                    subscription. You are authorizing and agreeing to pay these
                    charges by continuing.
                  </Typography>
                </LeftItem>
                <Item>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={handleConfirmClick}
                      disabled={submitting}
                    >
                      <SaveIcon style={{ marginRight: '5' }} />
                      Confirm Plan Change
                    </Button>
                    {submitting && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: 'secondary.main',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-9px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </Box>
                </Item>
              </>
            )
          ) : (
            <>
              <LeftItem>
                <Typography>
                  To change your subscription plan, select one of the plans
                  below.
                </Typography>
              </LeftItem>
              <Item>
                <Grid
                  container
                  direction='row'
                  justifyContent='center'
                  alignItems='flex-start'
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '0',
                  }}
                  rowSpacing={1}
                >
                  {availablePlans.map((plan) => (
                    <Grid item sx={6} key={plan.PlanID}>
                      <PlanCard
                        id={plan.PlanID}
                        code={plan.PlanCode}
                        name={plan.PlanName}
                        description={plan.PlanDescription}
                        currencyCode={plan.PlanCurrencyCode}
                        unitCost={plan.PlanUnitCost}
                        interval={plan.PlanInterval}
                        intervalLength={plan.PlanIntervalLength}
                        freeTrialInterval={plan.FreeTrialInterval}
                        freeTrialLength={plan.FreeTrialLength}
                        handleSelect={() =>
                          setSelecetedPlan({
                            PlanID: plan.PlanID,
                            PlanCode: plan.PlanCode,
                            PlanName: plan.PlanName,
                            PlanDescription: plan.PlanDescription,
                            PlanCurrencyCode: plan.PlanCurrencyCode,
                            PlanUnitCost: plan.PlanUnitCost,
                            PlanInterval: plan.PlanInterval,
                            PlanIntervalLength: plan.PlanIntervalLength,
                            FreeTrialInterval: plan.FreeTrialInterval,
                            FreeTrialLength: plan.FreeTrialLength,
                          })
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Item>
            </>
          )}
        </Stack>
      ) : (
        <Grid container spacing={10}>
          <Grid item xs={12}>
            <Typography>No available plans were found</Typography>
          </Grid>
        </Grid>
      )}
      <Stack alignItems='center' justifyContent='center'>
        {errorMessage && (
          <Item>
            <Typography sx={{ color: 'error.main' }}>{errorMessage}</Typography>
          </Item>
        )}
        <Item>
          <Button
            type={'reset'}
            color='secondary'
            disabled={submitting}
            variant='contained'
            onClick={callback}
          >
            <CancelIcon style={{ marginRight: '5' }} />
            Cancel
          </Button>
        </Item>
      </Stack>
    </>
  );
}
